import ErrorMessage from "components/common/ErrorMessage";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import { Select } from "components/common/Select";
import { ListLayout } from "components/common/ListLayout";
import TableHeaderItem from "components/common/ListLayout/TableHeaderItem";
import TranslationListTable from "components/dashboard/TranslationListTable";
import GraphCard from "components/dashboard/GraphCard";
import { fetchDashboardAnalytics } from "slices/dashboardSlice";
import { fetchDashboardAppointments } from "slices/dashboardSlice";
import { fetchAppointments } from "slices/appointmentSlice";
import { DashboardSelector } from "slices/dashboardSlice";
import { fetchLanguages } from "slices/languageSlice";
import { languageSelector } from "slices/languageSlice";
import { setError } from "slices/dashboardSlice";
import { adminEditDayOff, adminDeleteDayOff, adminEditLunch, adminDeleteLunch, adminEditTravelTime, adminDeleteTravelTime } from "slices/dashboardSlice";
import EditDayOffModal from "components/appointment/EditDayOffModal";
import EditTravelTimeModal from "components/appointment/EditTravelTimeModal";
import EditLunchModal from "components/appointment/EditLunchModal";
import { AsyncPaginate } from 'react-select-async-paginate';
import { selectCustomStyles } from 'components/common/Select';
import FullPageLoader from "components/common/FullPageLoader";
import { usePermissions } from "layouts/Admin";
import { fetchFacilities } from "slices/facilitiesSlice";
import { departmentsSelector } from "slices/departmentsSlice";
import { facilitiesSelector } from "slices/facilitiesSlice";
import { fetchDepartments } from "slices/departmentsSlice";
import { LegendButton } from "components/common/TableColor";
import { legends } from "components/common/TableColor";
import { fetchSchools } from "slices/schoolSlice";
import { schoolSelector } from "slices/schoolSlice";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import ConfirmationModal from "components/common/ConfirmationMessage";
import DateRangeFilter from "components/common/DateRangeFilter";
import { fetchInterpreterOptions } from "slices/interpreterSlice";
import { convertTimeToUTC } from "helpers/convertTimeToUTC";
import { fetchDashboardGraph } from "slices/dashboardSlice";
import { Link, useNavigate } from "react-router-dom";
import { fetchTranslationsList } from "slices/translationSlice";
import { appointmentsSelector } from "slices/appointmentSlice";
import { getBackgroundStyle } from "components/common/TableColor";


const LUNCH_ROW_STYLE = { background: '#2e7d3222' };
const LUNCH_EYE_STYLE = { background: '#2e7d32' };
const DAY_OFF_ROW_STYLE = { background: '#fd7e1433' };
const DAY_OFF_EYE_STYLE = { background: '#fd7e14' };
const TRAVEL_TIME_ROW_STYLE = { background: '#ff69b433' };
const TRAVEL_TIME_EYE_STYLE = { background: '#e91e8c' };

const statusOptions = [
  { value: 'open',      label: 'Open' },
  { value: 'assigned',  label: 'Assigned' },
  { value: 'pending',   label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'declined',  label: 'Declined' },
  { value: 'day_off',     label: 'Day Off' },
  { value: 'travel_time', label: 'Travel Time' },
  { value: 'lunch',       label: 'Lunch' },
];

function Dashboard() {
  const { checkPermission } = usePermissions()
  const { appointments } = useSelector(appointmentsSelector);
  const dispatch = useDispatch()
  const { dashboard, last_page: totalPages, loader, total, current_page, per_page: pageLength } = useSelector(DashboardSelector);
  const appointmentData = useSelector(appointmentsSelector);
  const { language: languages } = useSelector(languageSelector);
  const { school: schools } = useSelector(schoolSelector);
  const { departments } = useSelector(departmentsSelector);
  const { facilities } = useSelector(facilitiesSelector);
  const [downloading, setDownloading] = useState(false);
  const [languageState, setLanguageState] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [currentPageNum, setCurrentPageNum] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    sortBy: 'date',
    sortDirection: 'asc',
  });
  const interpreters = useSelector((state) => state.interpreter.interpreter);
  const navigate = useNavigate();
  const translationsState = useSelector((state) => state.translation) || {};
  const { translations = { data: [], total: 0 }, total: totalTranslations = 0, current_page: currentPageTranslation = 1, per_page = 10 } = translationsState;
  console.log("translationsState", translationsState);

  const defaultValues = {
    status: 'all',
    start_date: moment().format('YYYY-MM-DD'),
    end_date: moment().format('YYYY-MM-DD'),
    interpreter: null,
    status_filter: [], // empty = All
  };

  const { clearErrors, handleSubmit, watch, formState: { errors }, control, setValue, setError: setErrors, reset, getValues } = useForm({
    defaultValues: defaultValues
  });

  const [activeTab, setActiveTab] = useState('appointments');
  const [filters, setFilters] = useState({ ...defaultValues, include_lunch: true });
  const lunchRecords = appointmentData?.lunchRecords ?? [];
  const [editDayOffModal, setEditDayOffModal] = useState({ show: false, item: null });
  const [deleteDayOffModal, setDeleteDayOffModal] = useState({ show: false, item: null });
  const [editTravelTimeModal, setEditTravelTimeModal] = useState({ show: false, item: null });
  const [deleteTravelTimeModal, setDeleteTravelTimeModal] = useState({ show: false, item: null });
  const [editLunchModal, setEditLunchModal] = useState({ show: false, item: null });
  const [deleteLunchModal, setDeleteLunchModal] = useState({ show: false, item: null });

  // Derive show states from form field — no independent useState
  const statusFilter = watch('status_filter');
  const showLunch      = !statusFilter?.length || statusFilter.includes('lunch');
  const showDayOff     = !statusFilter?.length || statusFilter.includes('day_off');
  const showTravelTime = !statusFilter?.length || statusFilter.includes('travel_time');

  // Update currentPageSize from actual API response
  useEffect(() => {
    if (appointmentData?.per_page) {
      setCurrentPageSize(appointmentData.per_page);
    }
  }, [appointmentData?.per_page]);

  useEffect(() => {
    dispatch(fetchDashboardAnalytics());
    dispatch(fetchDashboardGraph());

    const defaultTabVal = 'appointments';
    setActiveTab(defaultTabVal);

    const today = moment().format('YYYY-MM-DD');
    let apiParams = {
      pageLength: 100,
      sortBy: "date",
      sortDirection: "asc",
      start_date: today,
      end_date: today,
      include_lunch: true,
    };

    if (defaultTabVal === 'appointments') {
      setFilters(apiParams);
      setCurrentPageSize(10);
      dispatch(fetchAppointments(1, apiParams));
    }
  }, [dispatch]);

  const fetchList = (page = 1, params = {}) => {
    const today = moment().format('YYYY-MM-DD');
    const finalParams = {
      pageLength: params?.pageLength || 10,
      sortBy: "id",
      sortDirection: "desc",
      date: today,
      search: params?.search || "",
    };
    return fetchTranslationsList(page, finalParams);
  };

  const handleTabClick = (tabType) => {
    setActiveTab(tabType);
    const today = moment().format('YYYY-MM-DD');
    let apiParams = {
      pageLength: 100,
      sortBy: "date",
      sortDirection: "asc",
      start_date: today,
      end_date: today
    };
    if (tabType === 'appointments') {
      apiParams = { ...apiParams, type: [0, 1, 4], include_lunch: true };
      setFilters(apiParams);
      dispatch(fetchAppointments(1, apiParams));
    } else if (tabType === 'bilingual') {
      apiParams = { ...apiParams, type: 2 };
      setFilters(apiParams);
      dispatch(fetchAppointments(1, apiParams));
    } else if (tabType === 'translations') {
      setFilters(apiParams);
      dispatch(fetchTranslationsList(1, apiParams));
    }
  };

  const graphData = dashboard?.graph;
  const type0Completed = graphData?.types?.[0]?.map(item => item.completed);
  const type0Assigned  = graphData?.types?.[0]?.map(item => item.assigned);
  const type0Declined  = graphData?.types?.[0]?.map(item => item.declined);
  const type0Cancelled = graphData?.types?.[0]?.map(item => item.cancelled);
  const type0Pending   = graphData?.types?.[0]?.map(item => item.pending);
  const type0Available = graphData?.types?.[0]?.map(item => item.available);

  const type1Completed = graphData?.types?.[1]?.map(item => item.completed);
  const type1Assigned  = graphData?.types?.[1]?.map(item => item.assigned);
  const type1Declined  = graphData?.types?.[1]?.map(item => item.declined);
  const type1Cancelled = graphData?.types?.[1]?.map(item => item.cancelled);
  const type1Pending   = graphData?.types?.[1]?.map(item => item.pending);
  const type1Available = graphData?.types?.[1]?.map(item => item.available);

  const type2Completed = graphData?.types?.[2]?.map(item => item.completed);
  const type2Assigned  = graphData?.types?.[2]?.map(item => item.assigned);
  const type2Declined  = graphData?.types?.[2]?.map(item => item.declined);
  const type2Cancelled = graphData?.types?.[2]?.map(item => item.cancelled);
  const type2Pending   = graphData?.types?.[2]?.map(item => item.pending);
  const type2Available = graphData?.types?.[2]?.map(item => item.available);

  const type3Completed = graphData?.types?.[3]?.map(item => item.completed);
  const type3Assigned  = graphData?.types?.[3]?.map(item => item.assigned);
  const type3Declined  = graphData?.types?.[3]?.map(item => item.declined);
  const type3Cancelled = graphData?.types?.[3]?.map(item => item.cancelled);
  const type3Pending   = graphData?.types?.[3]?.map(item => item.pending);
  const type3Available = graphData?.types?.[3]?.map(item => item.available);

  const [isFourteenDays, setIsFourteenDays] = useState(false);
  const tableRef = useRef(null);

  const REGISTERED_STATUSES = ['assigned', 'pending', 'completed', 'cancelled', 'declined'];

  const viewAppointments = (type) => {
    if (type === 0 || type === 1) {
      const today = moment().format('YYYY-MM-DD');
      const filterParams = {
        start_date: today,
        end_date: today,
        type: type,
        pageLength: 100,
        sortBy: "date",
        sortDirection: "asc",
        status: REGISTERED_STATUSES,
        include_lunch: false,
      };
      setValue("start_date", today);
      setValue("end_date", today);
      setValue("type", type);
      setValue("status_filter", REGISTERED_STATUSES);
      setFilters(filterParams);
      dispatch(fetchAppointments(1, filterParams));
      tableRef.current?.scrollIntoView({ behavior: "smooth" });
    } else if (type === 2) {
      navigate("/billingual-requests", { state: { filterByToday: true } });
    } else if (type === 3) {
      navigate("/translations", { state: { filterByToday: true } });
    }
  };

  const nextFourteenDaysAppointments = (type) => {
    if (type === 0 || type === 1) {
      const tomorrow = moment().add(1, 'day').format('YYYY-MM-DD');
      const fourteenDaysAfter = moment().add(14, 'days').format('YYYY-MM-DD');
      const filterParams = {
        start_date: tomorrow,
        end_date: fourteenDaysAfter,
        type: type,
        pageLength: 100,
        sortBy: "date",
        sortDirection: "asc",
        status: REGISTERED_STATUSES,
        include_lunch: false,
      };
      setValue("start_date", tomorrow);
      setValue("end_date", fourteenDaysAfter);
      setValue("type", type);
      setValue("status_filter", REGISTERED_STATUSES);
      setFilters(filterParams);
      dispatch(fetchAppointments(1, filterParams));
      tableRef.current?.scrollIntoView({ behavior: "smooth" });
    } else if (type === 2) {
      navigate("/billingual-requests", { state: { filterByFourteen: true } });
    } else if (type === 3) {
      navigate("/translations", { state: { filterByFourteen: true } });
    }
  };

  const viewToCoverAppointments = (type, period) => {
    if (type === 0 || type === 1) {
      const today = moment().format('YYYY-MM-DD');
      const start = period === 'today' ? today : moment().add(1, 'day').format('YYYY-MM-DD');
      const end   = period === 'today' ? today : moment().add(14, 'days').format('YYYY-MM-DD');
      const filterParams = {
        start_date: start,
        end_date: end,
        type: type,
        pageLength: 100,
        sortBy: "date",
        sortDirection: "asc",
        status: ['open'],
        include_lunch: false,
      };
      setValue("start_date", start);
      setValue("end_date", end);
      setValue("type", type);
      setValue("status_filter", ['open']);
      setFilters(filterParams);
      dispatch(fetchAppointments(1, filterParams));
      tableRef.current?.scrollIntoView({ behavior: "smooth" });
    } else if (type === 2) {
      navigate("/billingual-requests", {
        state: period === 'today' ? { filterByToday: true } : { filterByFourteen: true },
      });
    } else if (type === 3) {
      navigate("/translations", {
        state: period === 'today' ? { filterByToday: true } : { filterByFourteen: true },
      });
    }
  };

  useEffect(() => {
    (async () => {
      const result = await dispatch(fetchInterpreterOptions());
      console.log("Prefetched interpreters:", result);
    })();
  }, [dispatch]);

  // Update languageState when languages change
  useEffect(() => {
    setLanguageState(
      languages?.map(val => ({
        label: val?.name || "",
        value: val?.id || ""
      })) || []
    );
  }, [languages]);

  // ========== LEGENDS (display only) ==========
  const filteredStatuses = React.useMemo(() => {
    const temp_array = [...legends];
    temp_array.unshift({ value: "all", name: "All", id: 0, color: "#3176A0" });
    return temp_array;
  }, [legends]);

  // ========== LANGUAGES DROPDOWN ==========
  const filteredLanguagesLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(fetchLanguages(page, { pageLength: 20, sortBy: "id", sortDirection: "asc", search: inputValue }, true, true));
    return {
      options: result?.data?.map(language => ({ value: language.id, label: language.name })),
      hasMore: page !== result?.last_page,
      additional: { page: page + 1 },
    };
  };

  // ========== SCHOOL DROPDOWN ==========
  const filteredSchools = React.useMemo(() =>
    schools?.map(school => ({ value: school?.id, label: school?.name })) || [], [schools]);

  const filteredSchoolLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(fetchSchools(page, { pageLength: 20, sortBy: "id", sortDirection: "asc", search: inputValue }, true, true));
    return {
      options: result?.data?.map((item) => ({ value: item.id, label: item.name })),
      hasMore: page !== result?.last_page,
      additional: { page: page + 1 },
    };
  };

  // ========== INTERPRETER DROPDOWN ==========
  const interpreterOptions = React.useMemo(
    () => interpreters?.map((i) => ({
      value: i.id,
      label: i.user?.name || `${i.first_name ?? ""} ${i.last_name ?? ""}`.trim() || "Unknown",
    })) || [],
    [interpreters]
  );

  const interpreterLoadOptions = async (inputValue, loadedOptions, { page }) => {
    return await dispatch(fetchInterpreterOptions(page, {
      pageLength: 100, sortBy: "id", sortDirection: "asc", search: inputValue,
    }));
  };

  // ========== FORM HANDLERS ==========
  const handleReset = () => {
    const today = moment().format("YYYY-MM-DD");
    setLanguageState([]);
    reset({
      start_date: today,
      end_date: today,
      appointment_id: "",
      interpreter: null,
      school_id: null,
      type: null,
      language_ids: [],
      status_filter: [],
    });
    const emptyFilters = {
      start_date: today,
      end_date: today,
      pageLength: 100,
      sortBy: "date",
      sortDirection: "asc",
      include_lunch: true,
    };
    setFilters(emptyFilters);
    setCurrentPageSize(10);
    dispatch(fetchAppointments(1, emptyFilters));
    clearErrors();
  };

  console.log(total, 'dashboard');

  const handleConfirmDeleteDayOff = () => {
    dispatch(adminDeleteDayOff(deleteDayOffModal.item.id, (success, message) => {
      if (success) {
        toast.success(message, { className: 'successToast' });
        setDeleteDayOffModal({ show: false, item: null });
        dispatch(fetchAppointments(1, { ...filters, include_lunch: true }));
      } else {
        toast.error(message, { className: 'errorToast' });
      }
    }));
  };

  const handleConfirmDeleteTravelTime = () => {
    dispatch(adminDeleteTravelTime(deleteTravelTimeModal.item.id, (success, message) => {
      if (success) {
        toast.success(message, { className: 'successToast' });
        setDeleteTravelTimeModal({ show: false, item: null });
        dispatch(fetchAppointments(1, { ...filters, include_lunch: true }));
      } else {
        toast.error(message, { className: 'errorToast' });
      }
    }));
  };

  const handleConfirmDeleteLunch = () => {
    const numericId = String(deleteLunchModal.item.id).replace('lunch_', '');
    dispatch(adminDeleteLunch(numericId, (success, message) => {
      if (success) {
        toast.success(message, { className: 'successToast' });
        setDeleteLunchModal({ show: false, item: null });
        dispatch(fetchAppointments(1, { ...filters, include_lunch: true }));
      } else {
        toast.error(message, { className: 'errorToast' });
      }
    }));
  };

  // ========== TABLE HANDLERS ==========
  const handleChange = (e) => {
    const id = e.target.dataset.value;
    setSelectedItems(
      selectedItems.includes(id)
        ? selectedItems.filter((item) => item != id)
        : [...selectedItems, id]
    );
  };

  const handleToggleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = appointmentData?.appointments?.map((item) => item.id.toString()) || [];
      setSelectedItems(allIds);
      setSelectAll(true);
    } else {
      setSelectedItems([]);
      setSelectAll(false);
    }
  };

  const hideConfirmDialog = () => { setDeleteModal(false); };

  const handleRemoveItems = () => {
    hideConfirmDialog();
    setSelectedItems([]);
    setSelectAll(false);
  };

  const handleAddAppointmentPopUp = (item) => {};

  const renderTableHeader = React.useCallback(() => {
    const callBack = (dir, sortBy) => {
      setSortConfig({ sortBy, sortDirection: dir });
      dispatch(fetchAppointments(currentPageNum, {
        ...filters,
        pageLength: currentPageSize,
        search: searchTerm,
        sortBy: sortBy,
        sortDirection: dir,
        include_lunch: true,
      }));
    };
    return (
      <tr>
        <th>#</th>
        <th>
          <TableHeaderItem title="Timestamp" sortBy="created_at" sortDirection={sortConfig.sortDirection} activeSortBy={sortConfig.sortBy} callBack={callBack} />
        </th>
        <th>
          <TableHeaderItem title="Job No.#" sortBy="appid" sortDirection={sortConfig.sortDirection} activeSortBy={sortConfig.sortBy} callBack={callBack} />
        </th>
        <th>Person Completing the Form</th>
        <th>Format for Meeting</th>
        <th>
          <TableHeaderItem title="Type of Meeting" sortBy="type" sortDirection={sortConfig.sortDirection} activeSortBy={sortConfig.sortBy} callBack={callBack} />
        </th>
        <th>
          <TableHeaderItem title="Meeting Date" sortBy="date" sortDirection={sortConfig.sortDirection} activeSortBy={sortConfig.sortBy} callBack={callBack} />
        </th>
        <th>Meeting Start Time</th>
        <th>Meeting End Time</th>
        <th>Meeting Location</th>
        <th>Interpreter should report to whom?</th>
        <th>
          <TableHeaderItem title="Name of the assigned interpreter" sortBy="interpreter" sortDirection={sortConfig.sortDirection} activeSortBy={sortConfig.sortBy} callBack={callBack} />
        </th>
        <th>Student's Name</th>
        <th>
          <TableHeaderItem title="School Student Currently Attends" sortBy="school" sortDirection={sortConfig.sortDirection} activeSortBy={sortConfig.sortBy} callBack={callBack} />
        </th>
        <th>Parent or Guardian's Name</th>
        <th>Parent or Guardian's Phone Number</th>
        <th>
          <TableHeaderItem title="Language" sortBy="language" sortDirection={sortConfig.sortDirection} activeSortBy={sortConfig.sortBy} callBack={callBack} />
        </th>
        <th>
          <TableHeaderItem title="Status" sortBy="status" sortDirection={sortConfig.sortDirection} activeSortBy={sortConfig.sortBy} callBack={callBack} />
        </th>
      </tr>
    );
  }, [selectAll, searchTerm, sortConfig, currentPageNum, selectedItems, appointments, filters, currentPageSize]);

  const renderTableContent = React.useCallback(() => {
    const getEyeStyle = (status) => {
      if (status === 'assigned') { return { background: '#785c99' }; }
      else if (status === 'open' || status === 'available') { return { background: '#ffb82a' }; }
      else if (status === 'dnc') { return { background: '#3176a0' }; }
      else if (status === 'cnc') { return { background: '#9623e0' }; }
      else if (status === 'completed') { return { background: '#1c2b2c' }; }
      else if (status === 'cancelled') { return { background: '#e33a31' }; }
      else if (status === 'vendor_declined' || status === 'declined') { return { background: '#b51a12' }; }
      else if (status === 'pending') { return { background: '#3176a0' }; }
      else if (status === 'day_off') { return DAY_OFF_EYE_STYLE; }
      return { background: '#785c99' }; // default fallback
    };

    const lunchRows = showLunch ? lunchRecords?.map((lunch) => {
      const dateLabel = lunch?.date ? moment(lunch.date, 'YYYY-MM-DD').format('MMM D, YYYY') : 'N/A';
      const timeLabel = lunch?.start_time && lunch?.end_time
        ? `${moment(lunch.start_time, 'HH:mm').format('h:mm A')} – ${moment(lunch.end_time, 'HH:mm').format('h:mm A')}`
        : lunch?.start_time ? moment(lunch.start_time, 'HH:mm').format('h:mm A') : 'N/A';
      return (
        <tr key={lunch?.id}>
          <td className="p-0" style={LUNCH_ROW_STYLE}>
            <div className="eye_area" style={LUNCH_EYE_STYLE}>
              <i className="fa-solid fa-utensils"></i>
            </div>
          </td>
          <td style={LUNCH_ROW_STYLE}>{lunch?.created_at ? moment(lunch.created_at).format('MMM D, YYYY HH:mm') : '—'}</td>
          <td style={LUNCH_ROW_STYLE}>LUNCH</td>
          <td style={LUNCH_ROW_STYLE}>—</td>
          <td style={LUNCH_ROW_STYLE}>—</td>
          <td style={LUNCH_ROW_STYLE}>Lunch Break</td>
          <td style={LUNCH_ROW_STYLE}>{dateLabel}</td>
          <td style={LUNCH_ROW_STYLE}>{lunch?.start_time ? moment(lunch.start_time, 'HH:mm').format('h:mm A') : '—'}</td>
          <td style={LUNCH_ROW_STYLE}>{lunch?.end_time ? moment(lunch.end_time, 'HH:mm').format('h:mm A') : '—'}</td>
          <td style={LUNCH_ROW_STYLE}>—</td>
          <td style={LUNCH_ROW_STYLE}>—</td>
          <td style={LUNCH_ROW_STYLE}>{lunch?.interpreter_name || '—'}</td>
          <td style={LUNCH_ROW_STYLE}>—</td>
          <td style={LUNCH_ROW_STYLE}>—</td>
          <td style={LUNCH_ROW_STYLE}>—</td>
          <td style={LUNCH_ROW_STYLE}>—</td>
          <td style={LUNCH_ROW_STYLE}>—</td>
          <td style={LUNCH_ROW_STYLE}>
            <div className="d-flex gap-2 justify-content-between align-items-center">
              <span>On Lunch</span>
              <div className="d-flex gap-2">
                <button type="button" title="Edit" onClick={() => setEditLunchModal({ show: true, item: lunch })}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#555', padding: '2px 6px' }}>
                  <i className="fa-solid fa-pen-to-square"></i>
                </button>
                <button type="button" title="Delete" onClick={() => setDeleteLunchModal({ show: true, item: lunch })}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c0392b', padding: '2px 6px' }}>
                  <i className="fa-solid fa-trash"></i>
                </button>
              </div>
            </div>
          </td>
        </tr>
      );
    }) : [];

    return (
      <>
        {appointmentData?.appointments && appointmentData?.appointments?.map((appointment, index) => {
          // eslint-disable-next-line eqeqeq
          if (!showDayOff && appointment?.type == 4) return null;
          // eslint-disable-next-line eqeqeq
          if (!showTravelTime && appointment?.type == 5) return null;

          const typeLabels = {
            0: "Interpreting Request",
            1: "After Hours Request",
            2: "Bilingual Exams Request",
            4: "Day Off",
            5: "Travel Time",
          };

          const rawDatetime = appointment?.date + " " + appointment?.start_time;
          const formattedDate = moment(rawDatetime, "YYYY-MM-DD HH:mm:ss").format("MMM D, YYYY HH:mm:ss");

          // eslint-disable-next-line eqeqeq
          if (appointment?.type == 4) {
            const interpreterName = appointment?.interpreter
              ? `${appointment.interpreter.first_name || ''} ${appointment.interpreter.last_name || ''}`.trim()
              : 'N/A';
            const dayDateLabel = appointment?.date ? moment(appointment.date, 'YYYY-MM-DD').format('MMM D, YYYY') : 'N/A';
            return (
              <tr key={appointment.id}>
                <td className="p-0" style={DAY_OFF_ROW_STYLE}>
                  <div className="eye_area" style={DAY_OFF_EYE_STYLE}>
                    <i className="fa-solid fa-ban"></i>
                  </div>
                </td>
                <td style={DAY_OFF_ROW_STYLE}>{appointment?.created_at ? moment(appointment.created_at).format('MMM D, YYYY HH:mm') : '—'}</td>
                <td style={DAY_OFF_ROW_STYLE}>—</td>
                <td style={DAY_OFF_ROW_STYLE}>—</td>
                <td style={DAY_OFF_ROW_STYLE}>—</td>
                <td style={DAY_OFF_ROW_STYLE}>Day Off</td>
                <td style={DAY_OFF_ROW_STYLE}>{dayDateLabel}</td>
                <td style={DAY_OFF_ROW_STYLE}>
                  {appointment?.start_time && appointment?.end_time
                    ? `${moment(appointment.start_time, 'HH:mm:ss').format('h:mm A')} - ${moment(appointment.end_time, 'HH:mm:ss').format('h:mm A')}`
                    : appointment?.start_time ? moment(appointment.start_time, 'HH:mm:ss').format('h:mm A') : '—'}
                </td>
                <td style={DAY_OFF_ROW_STYLE}>
                  {appointment?.start_time && appointment?.end_time ? (() => {
                    const mins = moment(appointment.end_time, 'HH:mm:ss').diff(moment(appointment.start_time, 'HH:mm:ss'), 'minutes');
                    if (mins <= 0) return '—';
                    const h = Math.floor(mins / 60), m = mins % 60;
                    if (h === 0) return `${m} min`;
                    if (m === 0) return `${h} hr`;
                    return `${h} hr ${m} min`;
                  })() : '—'}
                </td>
                <td style={DAY_OFF_ROW_STYLE}>—</td>
                <td style={DAY_OFF_ROW_STYLE}>—</td>
                <td style={DAY_OFF_ROW_STYLE}>{interpreterName}</td>
                <td style={DAY_OFF_ROW_STYLE}>—</td>
                <td style={DAY_OFF_ROW_STYLE}>—</td>
                <td style={DAY_OFF_ROW_STYLE}>—</td>
                <td style={DAY_OFF_ROW_STYLE}>—</td>
                <td style={DAY_OFF_ROW_STYLE}>—</td>
                <td style={DAY_OFF_ROW_STYLE}>
                  <div className="d-flex gap-2 justify-content-between align-items-center">
                    <span>Day Off</span>
                    <div className="d-flex gap-2">
                      <button type="button" title="Edit" onClick={() => setEditDayOffModal({ show: true, item: appointment })}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#555', padding: '2px 6px' }}>
                        <i className="fa-solid fa-pen-to-square"></i>
                      </button>
                      <button type="button" title="Delete" onClick={() => setDeleteDayOffModal({ show: true, item: appointment })}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c0392b', padding: '2px 6px' }}>
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            );
          }

          // eslint-disable-next-line eqeqeq
          if (appointment?.type == 5) {
            const interpreterName = appointment?.interpreter
              ? `${appointment.interpreter.first_name || ''} ${appointment.interpreter.last_name || ''}`.trim()
              : 'N/A';
            const ttDateLabel = appointment?.date ? moment(appointment.date, 'YYYY-MM-DD').format('MMM D, YYYY') : 'N/A';
            return (
              <tr key={appointment.id}>
                <td className="p-0" style={TRAVEL_TIME_ROW_STYLE}>
                  <div className="eye_area" style={TRAVEL_TIME_EYE_STYLE}>
                    <i className="fa-solid fa-car"></i>
                  </div>
                </td>
                <td style={TRAVEL_TIME_ROW_STYLE}>{appointment?.created_at ? moment(appointment.created_at).format('MMM D, YYYY HH:mm') : '—'}</td>
                <td style={TRAVEL_TIME_ROW_STYLE}>—</td>
                <td style={TRAVEL_TIME_ROW_STYLE}>—</td>
                <td style={TRAVEL_TIME_ROW_STYLE}>—</td>
                <td style={TRAVEL_TIME_ROW_STYLE}>Travel Time</td>
                <td style={TRAVEL_TIME_ROW_STYLE}>{ttDateLabel}</td>
                <td style={TRAVEL_TIME_ROW_STYLE}>
                  {appointment?.start_time && appointment?.end_time
                    ? `${moment(appointment.start_time, 'HH:mm:ss').format('h:mm A')} - ${moment(appointment.end_time, 'HH:mm:ss').format('h:mm A')}`
                    : appointment?.start_time ? moment(appointment.start_time, 'HH:mm:ss').format('h:mm A') : '—'}
                </td>
                <td style={TRAVEL_TIME_ROW_STYLE}>
                  {appointment?.start_time && appointment?.end_time ? (() => {
                    const mins = moment(appointment.end_time, 'HH:mm:ss').diff(moment(appointment.start_time, 'HH:mm:ss'), 'minutes');
                    if (mins <= 0) return '—';
                    const h = Math.floor(mins / 60), m = mins % 60;
                    if (h === 0) return `${m} min`;
                    if (m === 0) return `${h} hr`;
                    return `${h} hr ${m} min`;
                  })() : '—'}
                </td>
                <td style={TRAVEL_TIME_ROW_STYLE}>—</td>
                <td style={TRAVEL_TIME_ROW_STYLE}>—</td>
                <td style={TRAVEL_TIME_ROW_STYLE}>{interpreterName}</td>
                <td style={TRAVEL_TIME_ROW_STYLE}>—</td>
                <td style={TRAVEL_TIME_ROW_STYLE}>—</td>
                <td style={TRAVEL_TIME_ROW_STYLE}>—</td>
                <td style={TRAVEL_TIME_ROW_STYLE}>—</td>
                <td style={TRAVEL_TIME_ROW_STYLE}>—</td>
                <td style={TRAVEL_TIME_ROW_STYLE}>
                  <div className="d-flex gap-2 justify-content-between align-items-center">
                    <span>Travel Time</span>
                    <div className="d-flex gap-2">
                      <button type="button" title="Edit" onClick={() => setEditTravelTimeModal({ show: true, item: appointment })}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#555', padding: '2px 6px' }}>
                        <i className="fa-solid fa-pen-to-square"></i>
                      </button>
                      <button type="button" title="Delete" onClick={() => setDeleteTravelTimeModal({ show: true, item: appointment })}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c0392b', padding: '2px 6px' }}>
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            );
          }

          const assignedInterpreterName = appointment?.vendor_id != null && appointment?.vendor_interpreter
                  ? `${appointment.vendor_interpreter?.first_name || ""} ${appointment.vendor_interpreter?.last_name || ""} - Excel`.trim()
                  : appointment?.vendor_id != null && !appointment?.interpreter_id
                    ? `${appointment?.vendor_user?.name || "Vendor"} (Excel)`
                    : `${appointment?.interpreter?.first_name || ""} ${appointment?.interpreter?.last_name || ""}`.trim();

          return (
            <tr key={appointment.id}>
              <td className="p-0" style={getBackgroundStyle(appointment?.status)}>
                <div className="eye_area" style={getEyeStyle(appointment?.status)}>
                  <Link to={`/appointments/view/${appointment?.id}`}>
                    <i className="fa-solid fa-eye"></i>
                  </Link>
                </div>
              </td>
              <td style={getBackgroundStyle(appointment?.status)}>
                {appointment?.created_at ? moment(appointment.created_at).format('MMM D, YYYY HH:mm') : '—'}
              </td>
              <td style={getBackgroundStyle(appointment?.status)}>
                <Link className="link_preview" to={`/appointments/view/${appointment?.id}`}>
                  {appointment?.appid}
                </Link>
              </td>
              <td style={getBackgroundStyle(appointment?.status)}>
                {appointment?.appointment_details?.contact_person_name || '—'}
              </td>
              <td style={getBackgroundStyle(appointment?.status)}>
                {appointment?.meeting_format || '—'}
              </td>
              <td style={getBackgroundStyle(appointment?.status)}>
                {typeLabels[appointment?.type] || appointment?.appointment_details?.meeting_type || '—'}
              </td>
              <td style={getBackgroundStyle(appointment?.status)}>
                {appointment?.date ? moment(appointment.date).format("MMM D, YYYY") : "—"}
              </td>
              <td style={getBackgroundStyle(appointment?.status)}>
                {appointment?.start_time ? moment(appointment.start_time, "HH:mm:ss").format("h:mm A") : "—"}
              </td>
              <td style={getBackgroundStyle(appointment?.status)}>
                {appointment?.end_time ? moment(appointment.end_time, "HH:mm:ss").format("h:mm A") : "—"}
              </td>
              <td style={getBackgroundStyle(appointment?.status)}>
                {appointment?.room_number_at_site || '—'}
              </td>
              <td style={getBackgroundStyle(appointment?.status)}>
                {appointment?.appointment_details?.reporting_person || '—'}
              </td>
              <td style={getBackgroundStyle(appointment?.status)}>
                {assignedInterpreterName || '—'}
              </td>
              <td style={getBackgroundStyle(appointment?.status)}>
                {appointment?.appointment_details?.student_name || '—'}
              </td>
              <td style={getBackgroundStyle(appointment?.status)}>
                {appointment?.school?.name || "—"}
              </td>
              <td style={getBackgroundStyle(appointment?.status)}>
                {appointment?.appointment_details?.parent_name || '—'}
              </td>
              <td style={getBackgroundStyle(appointment?.status)}>
                {appointment?.appointment_details?.parent_contact_number || '—'}
              </td>
              <td style={{ ...getBackgroundStyle(appointment?.status), lineHeight: "1.5" }}>
                {appointment?.language?.name || appointment?.language_other || '—'}
              </td>
              <td style={getBackgroundStyle(appointment?.status)}>
                {appointment?.status || '—'}
              </td>
            </tr>
          );
        })}
        {lunchRows}
      </>
    );
  }, [appointmentData?.appointments, lunchRecords, showLunch, showDayOff, selectedItems, currentPageNum, pageLength, statusFilter]);

  return (
    <>
      <div className="content">
        <div className="row_main_head">
          <h2 className="main_heading">Dashboard</h2>
        </div>

        <div className="row_dash_1">
          <div className="card_table">
            <div className="interpreter p-4">
              <h2>Today</h2>
            </div>
            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">Today</th>
                    <th className="col-3 text-center">Registered</th>
                    <th className="col-3 text-center">To Cover</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.todays_data?.map((service, index) => (
                    <tr key={index}>
                      <td className="cursorPointer" onClick={() => viewAppointments(service?.value)}>{service?.type}</td>
                      <td className="cursorPointer text-center" onClick={() => viewAppointments(service?.value)}>{service?.assigned}</td>
                      <td className="cursorPointer text-center" onClick={() => viewToCoverAppointments(service?.value, 'today')}>{service?.pending}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card_table">
            <div className="interpreter p-4">
              <h2>Next 14 Days</h2>
            </div>
            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">Today</th>
                    <th className="col-3 text-center">Registered</th>
                    <th className="col-3 text-center">To Cover</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.fourteen_days?.map((service, index) => (
                    <tr key={index}>
                      <td className="cursorPointer" onClick={() => nextFourteenDaysAppointments(service?.value)}>{service?.type}</td>
                      <td className="cursorPointer text-center" onClick={() => nextFourteenDaysAppointments(service?.value)}>{service?.assigned}</td>
                      <td className="cursorPointer text-center" onClick={() => viewToCoverAppointments(service?.value, 'fourteen')}>{service?.pending}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="card_table p-4">
          <div className="card_table">
            {/* Legend — decorative only, not clickable */}
            <div className="button-sm forLegends p-3 gap-1" style={{ pointerEvents: 'none', cursor: 'default' }}>
              <LegendButton activeIds={[]} options={filteredStatuses} handleClick={() => {}} />
              <div className="border_btn">
                <button type="button" className="button-bu" style={{ backgroundColor: '#fd7e14' }}>
                  <p>Day Off</p>
                </button>
              </div>
              <div className="border_btn">
                <button type="button" className="button-bu" style={{ backgroundColor: '#2e7d32' }}>
                  <p>Lunch</p>
                </button>
              </div>
            </div>

            <form
              onSubmit={handleSubmit((values) => {
                const selected = values.status_filter || [];
                const nonLunch = selected.filter(s => s !== 'lunch');
                const hasLunchOnly = selected.includes('lunch') && nonLunch.length === 0;
                // When only Lunch is selected send an impossible status so the real-appointment
                // rows return empty and only the lunch records (include_lunch) are displayed.
                const statusParam = hasLunchOnly
                  ? ['__lunch_only__']
                  : nonLunch.length ? nonLunch : undefined;

                const params = values.appointment_id
                  ? { pageLength: 100, sortBy: 'id', sortDirection: 'desc', appointment_id: values.appointment_id, include_lunch: true }
                  : {
                      pageLength: 100,
                      start_date: values.start_date ? moment(values.start_date).format("YYYY-MM-DD") : null,
                      end_date: values.end_date ? moment(values.end_date).format("YYYY-MM-DD") : null,
                      interpreter: values.interpreter || null,
                      type: values.type,
                      language_ids: values.language_ids || [],
                      status: statusParam,
                      school_id: values.school_id ?? null,
                      include_lunch: true,
                    };
                setFilters(params);
                setSearchTerm("");
                dispatch(fetchAppointments(1, params));
              })}
              className="search_form_ad"
            >
              <div className="row-primary dahboard_filter appointments">
                <DateRangeFilter control={control} />

                <div className="row-primary">
                  <h2>Appointment ID</h2>
                  <Controller
                    name="appointment_id"
                    control={control}
                    render={({ field }) => (
                      <div className="input-basis basic_bases">
                        <input {...field} type="text" className="input-in type_inp" placeholder="Enter ID" />
                      </div>
                    )}
                  />
                </div>

                <div className="row-primary" style={{ flexBasis: "40%" }}>
                  <h2>Interpreter</h2>
                  <div style={{ flex: 1 }}>
                    <Controller
                      name="interpreter"
                      control={control}
                      defaultValue={null}
                      render={({ field }) => (
                        <div className="basic_bases">
                          <AsyncPaginate
                            menuPlacement="auto"
                            styles={{ ...selectCustomStyles }}
                            value={field.value ? interpreterOptions.find((opt) => String(opt.value) === String(field.value)) : null}
                            loadOptions={interpreterLoadOptions}
                            isClearable
                            escapeClearsValue
                            onChange={(obj) => field.onChange(obj?.value ?? null)}
                            additional={{ page: 1 }}
                          />
                        </div>
                      )}
                    />
                  </div>
                </div>

                <div className="row-primary">
                  <h2>School</h2>
                  <Controller
                    name="school_id"
                    control={control}
                    render={({ field }) => (
                      <div style={{ width: "100%" }}>
                        <AsyncPaginate
                          menuPlacement="auto"
                          styles={{ ...selectCustomStyles }}
                          value={field.value ? filteredSchools?.find((opt) => field.value == opt.value) : ""}
                          loadOptions={filteredSchoolLoadOptions}
                          isClearable
                          escapeClearsValue
                          onChange={(obj) => field.onChange(obj?.value)}
                          additional={{ page: 1 }}
                        />
                      </div>
                    )}
                  />
                </div>

                <div className="row-primary">
                  <h2>Type</h2>
                  <Controller
                    name="type"
                    control={control}
                    render={({ field }) => (
                      <div style={{ width: "100%" }}>
                        <AsyncPaginate
                          {...field}
                          styles={selectCustomStyles}
                          options={[
                            { value: 0, label: "Interpreting Request" },
                            { value: 1, label: "After Hours Request" },
                            { value: 4, label: "Day Off" },
                          ]}
                          isClearable
                          placeholder="Select Type"
                          onChange={(option) => field.onChange(option !== null ? option.value : null)}
                          value={
                            field.value !== null && field.value !== undefined
                              ? [
                                  { value: 0, label: "Interpreting Request" },
                                  { value: 1, label: "After Hours Request" },
                                  { value: 4, label: "Day Off" },
                                ].find((opt) => opt.value === field.value)
                              : null
                          }
                        />
                      </div>
                    )}
                  />
                </div>

                <div className="row-primary">
                  <h2>Languages</h2>
                  <Controller
                    name="language_ids"
                    control={control}
                    render={({ field }) => (
                      <div style={{ width: "100%" }} className="multiselect">
                        <AsyncPaginate
                          menuPlacement="auto"
                          isMulti
                          styles={{ ...selectCustomStyles }}
                          value={languageState}
                          loadOptions={filteredLanguagesLoadOptions}
                          isClearable
                          escapeClearsValue
                          onChange={(selected) => {
                            setLanguageState(selected);
                            field.onChange(selected.map((opt) => opt.value));
                          }}
                          additional={{ page: 1 }}
                        />
                      </div>
                    )}
                  />
                </div>

                <div className="row-primary">
                  <h2>Status</h2>
                  <Controller
                    name="status_filter"
                    control={control}
                    render={({ field }) => (
                      <div style={{ width: "100%" }} className="multiselect">
                        <AsyncPaginate
                          menuPlacement="auto"
                          isMulti
                          styles={{ ...selectCustomStyles }}
                          options={statusOptions}
                          placeholder="All Statuses"
                          isClearable
                          escapeClearsValue
                          onChange={(selected) => {
                            field.onChange((selected || []).map(opt => opt.value));
                          }}
                          value={(field.value || []).map(v => statusOptions.find(opt => opt.value === v)).filter(Boolean)}
                        />
                      </div>
                    )}
                  />
                </div>
              </div>

              <div className="d-flex align-items-center justify-content-between px-3">
                <div className="row-primary row_10 w-100">
                  <button type="submit" className="main_button green">Search</button>
                  <button type="button" className="main_button lightBlue" onClick={handleReset}>Reset</button>
                </div>
              </div>
            </form>
          </div>

          <div className="mt-4">
            <div ref={tableRef}>
              <ListLayout
                key={JSON.stringify(filters)}
                time={true}
                className={"appointment"}
                title=""
                buttonTitle="Add Appointment"
                renderTableHeader={renderTableHeader}
                renderTableContent={renderTableContent}
                addButtonDisabled={true}
                totalPages={appointmentData?.last_page || 1}
                fetchList={fetchAppointments}
                filtration={{ ...filters, pageLength: currentPageSize }}
                onPageSizeChange={(size) => {
                  setCurrentPageSize(size);
                  setFilters(prev => ({ ...prev, pageLength: size }));
                }}
                selectedItems={selectedItems}
                handleRemoveItems={handleRemoveItems}
                refAddutton={null}
                addForm={{ show: false, item: {} }}
                showConfirmDialog={() => setDeleteModal(true)}
                handleAddPopUp={handleAddAppointmentPopUp}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                loader={appointmentData?.loader || false}
                searchPlaceholder="Search Appointments ..."
                noOfItems={appointmentData?.total || 0}
                refAddPopUp="#addAppointmentPopUp"
                disableBootstrap={true}
                addformModal={false}
                styling="appointment"
              >
                {deleteModal && (
                  <ConfirmationModal
                    onClose={hideConfirmDialog}
                    isOpen={deleteModal}
                    onConfirm={handleRemoveItems}
                  />
                )}
              </ListLayout>
            </div>
          </div>
        </div>
      </div>

      {(loader || translationsState.loader) && <FullPageLoader />}

      {deleteDayOffModal.show && (
        <ConfirmationModal
          onClose={() => setDeleteDayOffModal({ show: false, item: null })}
          isOpen={deleteDayOffModal.show}
          onConfirm={handleConfirmDeleteDayOff}
          confirmationText="Are you sure you want to delete this day off?"
        />
      )}
      {editDayOffModal.show && (
        <EditDayOffModal
          show={editDayOffModal.show}
          item={editDayOffModal.item}
          isAdmin={true}
          onClose={() => setEditDayOffModal({ show: false, item: null })}
          onSuccess={() => {
            setEditDayOffModal({ show: false, item: null });
            dispatch(fetchAppointments(1, { ...filters, include_lunch: true }));
          }}
        />
      )}
      {deleteTravelTimeModal.show && (
        <ConfirmationModal
          onClose={() => setDeleteTravelTimeModal({ show: false, item: null })}
          isOpen={deleteTravelTimeModal.show}
          onConfirm={handleConfirmDeleteTravelTime}
          confirmationText="Are you sure you want to delete this travel time record?"
        />
      )}
      {editTravelTimeModal.show && (
        <EditTravelTimeModal
          show={editTravelTimeModal.show}
          item={editTravelTimeModal.item}
          onClose={() => setEditTravelTimeModal({ show: false, item: null })}
          onSuccess={() => {
            setEditTravelTimeModal({ show: false, item: null });
            dispatch(fetchAppointments(1, { ...filters, include_lunch: true }));
          }}
        />
      )}
      {deleteLunchModal.show && (
        <ConfirmationModal
          onClose={() => setDeleteLunchModal({ show: false, item: null })}
          isOpen={deleteLunchModal.show}
          onConfirm={handleConfirmDeleteLunch}
          confirmationText="Are you sure you want to delete this lunch record?"
        />
      )}
      {editLunchModal.show && (
        <EditLunchModal
          show={editLunchModal.show}
          item={editLunchModal.item}
          isAdmin={true}
          onClose={() => setEditLunchModal({ show: false, item: null })}
          onSuccess={() => {
            setEditLunchModal({ show: false, item: null });
            dispatch(fetchAppointments(1, { ...filters, include_lunch: true }));
          }}
        />
      )}
    </>
  );
}

export default Dashboard;
